<template>
  <div v-if="eventType.created_dt" class="project-event-type-form">
    <form>
      <section>
        <h1>{{ $t('projects.eventTypes.details.labels.eventTypeDetails') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.name') }}
              <span class="asterisk">*</span>
            </label>
            <InputField
              v-model="eventType.name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['name'] }"
              @input="$emit('update', { type: 'name', value: $event })"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.estimatedHoursOnsite') }}
            </label>
            <InputField
              v-model="eventType.estimated_hours_onsite"
              input-type="number"
              class="bordered"
              :class="{ error: formErrors['estimated_hours_onsite'] }"
              @input="$emit('update', { type: 'estimated_hours_onsite', value: $event })"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.checkInEscalationMinutes') }}
            </label>
            <InputField
              v-model="eventType.check_in_escalation_minutes"
              input-type="number"
              class="bordered"
              :class="{ error: formErrors['check_in_escalation_minutes'] }"
              @input="$emit('update', { type: 'check_in_escalation_minutes', value: $event })"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.checkOutEscalationMinutes') }}
            </label>
            <InputField
              v-model="eventType.check_out_escalation_minutes"
              input-type="number"
              class="bordered"
              :class="{ error: formErrors['check_out_escalation_minutes'] }"
              @input="$emit('update', { type: 'check_out_escalation_minutes', value: $event })"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.status') }}
              <span class="asterisk">*</span>
            </label>
            <DropdownSelect
              :menu-items="statuses"
              :selected-item="eventType.status"
              class="bordered"
              @select-item="$emit('update', { type: 'status', value: $event })"
            />
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.scopeOfWork') }}
              <span class="asterisk">*</span>
            </label>
            <TolTextArea
              v-model="eventType.scope_of_work"
              class="form-textarea"
              @change="$emit('update', { type: 'scope_of_work', value: $event.target.value })"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.labels.comments') }}
            </label>
            <TolTextArea
              v-model="eventType.comments"
              class="form-textarea"
              @change="$emit('update', { type: 'comments', value: $event.target.value })"
            />
          </div>
        </div>
        <div class="form-group">
          <div class="form-group-column">
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.labels.tesSupportPhoneNumber') }}
              </label>
              <InputField
                v-model="eventType.tes_support_phone_number"
                type="text"
                class="bordered"
                :class="{ error: formErrors['tes_support_phone_number'] }"
                @input="$emit('update', { type: 'tes_support_phone_number', value: $event })"
              />
            </div>
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.labels.tesSupportEmail') }}
              </label>
              <InputField
                v-model="eventType.tes_support_email"
                type="text"
                class="bordered"
                :class="{ error: formErrors['tes_support_email'] }"
                @input="$emit('update', { type: 'tes_support_email', value: $event })"
              />
            </div>
          </div>
          <div class="form-group-column">
            <div
              v-for="(contact, index) in eventType.client_support_contacts"
              :key="contact.id || index"
              class="contact-container"
            >
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.labels.clientSupport') }}
                </label>
                <DropdownSelect
                  :menu-items="clientSupportTypes"
                  :selected-item="contact.type"
                  class="bordered"
                  @select-item="
                    $emit('update', {
                      type: 'client_support_contacts',
                      value: {
                        index: contact.id ? null : index,
                        id: contact.id || null,
                        field: 'type',
                        value: $event
                      }
                    })
                  "
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.labels.clientSupportPhoneNumber') }}
                </label>
                <InputField
                  v-model="contact.phone_number"
                  type="text"
                  class="bordered"
                  :class="{ error: formErrors['client_support_contacts.phone_number'] }"
                  @input="
                    $emit('update', {
                      type: 'client_support_contacts',
                      value: {
                        index: contact.id ? null : index,
                        id: contact.id || null,
                        field: 'phone_number',
                        value: $event
                      }
                    })
                  "
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.labels.clientSupportEmail') }}
                </label>
                <InputField
                  v-model="contact.email"
                  type="text"
                  class="bordered"
                  :class="{ error: formErrors['client_support_contacts.email'] }"
                  @input="
                    $emit('update', {
                      type: 'client_support_contacts',
                      value: {
                        index: contact.id ? null : index,
                        id: contact.id || null,
                        field: 'email',
                        value: $event
                      }
                    })
                  "
                />
              </div>
            </div>
          </div>
          <div class="form-group-column">
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.labels.isOnsiteDocumentationRequired') }}
                <!-- eslint-disable -->
                <span class="asterisk">*</span>
              </label>
              <Checkbox
                size="tiny"
                :checked="eventType.is_onsite_documentation_required"
                @change="
                  $emit('update', {
                    type: 'is_onsite_documentation_required',
                    value: !eventType.is_onsite_documentation_required
                  })
                "
              />
            </div>
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.labels.isTMinus1CallRequired') }}
                <span class="asterisk">*</span>
              </label>
              <Checkbox
                size="tiny"
                :checked="eventType.is_t_minus_1_call_required"
                @change="
                  $emit('update', {
                    type: 'is_t_minus_1_call_required',
                    value: !eventType.is_t_minus_1_call_required
                  })
                "
              />
            </div>
          </div>
        </div>
      </section>
    </form>
  </div>
</template>

<script>
import { dateUtils } from '@/mixins/date-utils';
import Checkbox from '@/app/components/Checkbox';
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import TolTextArea from '@/app/components/TolTextArea';

export default {
  name: 'ProjectEventTypeDetailsForm',
  components: {
    Checkbox,
    DropdownSelect,
    InputField,
    TolTextArea
  },
  mixins: [dateUtils],
  props: {
    eventType: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      statuses: ['ACTIVE', 'INACTIVE', 'COMPLETED'],
      clientSupportTypes: ['PRIMARY', 'SECONDARY', 'ALTERNATE']
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    formErrors() {
      return this.$store.getters.ProjectEventTypeValidationErrors;
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.asterisk {
  color: red;
}

.project-event-type-form {
  form {
    width: 100%;

    section {
      padding: 0 2rem;
      margin-bottom: 20px;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 2px solid $blue;
      }

      h1,
      h2 {
        flex: 0 100%;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        &.right {
          justify-content: flex-end;
        }
        @media only screen and (min-width: 600px) {
          flex-direction: row;
          align-items: end;
        }
      }

      .form-control {
        padding: 10px 0;
        &.wide {
          width: 50rem;
          &.error {
            background-color: $modified-form;
          }
        }
        @media only screen and (min-width: 600px) {
          min-width: 12rem;
        }

        > label {
          display: block;
          font-weight: bold;
        }

        .form-textarea {
          min-width: 30rem;
        }
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }
      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }
    }
  }
  &.modified {
    border-left: 5px solid $orange;
  }

  .contact-container {
    display: flex;
  }

  .contact-container > div {
    margin-right: 1rem;
  }

  .error {
    color: $red;
    background-color: $modified-form;
  }
}
</style>
